import { type LinearConfiguratorState } from "~/features/configurator/Linear/redux/types";
import { type CsticUpdate } from "~/features/configurator/Linear/api/sendLinearConfiguratorUpdate";
import { groupByFn, isNonNull, splitBy } from "~/common/utils/reducers";
import { type LinearCstic } from "~/features/configurator/Linear/models";
import { type PriceInfo } from "~/domains/pricing/api/PriceInfo";
import { selectPrice } from "~/utils/pricePicker";
import { type PriceData } from "~/domains/pricing/types/PriceData";

export interface ConfiguratorApiCall {
  carriagesConfiguratorData?: CarriageConfiguratorApiData;
  railsConfiguratorData?: RailConfiguratorApiData;
  linearConfiguratorData?: UnitConfiguratorApiData;
  shaftConfiguratorData?: ShaftConfigurationData;
  kbKey: {
    productCode: string;
  };
  configId: string;
  selectedGroup: string;
  autoExpand: boolean;
  groupIdToDisplay: string | boolean;
  quantity: number;
  groupToDisplay: {
    groupIdPath: string;
    path: string;
  };
  groupIdToToggle: string;
  groupIdToToggleInSpecTree: string;
  forceExpand: boolean;
  hideImageGallery: boolean;

  cpqAction?: string;
  focusId?: string;
  groups?: UiGroupData[];
  pricing?: {
    basePrice: PriceData;
  };
  priceReference: string;
}

export interface UiGroupData {
  id: string;
  visited?: boolean;
  cstics?: CsticData[];
  subGroups?: UiGroupData[];
}

export interface CsticData {
  key: string;
  name: string;
  value: string;
  formattedValue: string;
  forceUpdate: boolean;
}

export interface LCSystemCommonConfiguratorApiData {
  currentStepNumber: number;
  highestVisitedStepNumber: number;
  description?: string;
  mediaUrl?: string;
}

export interface UnitConfiguratorApiData extends LCSystemCommonConfiguratorApiData {
  length?: number;
  resetCarriageDetails?: boolean;
  resetCarriageId?: string;
  customSegmentLengthSelected?: boolean;
  customSecondSegmentLengthSelected?: boolean;
  enableCarriageEdit?: boolean[];
}

export interface CarriageConfiguratorApiData extends LCSystemCommonConfiguratorApiData {
  enableConfigureCarriageValues?: boolean;
}

export interface RailConfiguratorApiData extends LCSystemCommonConfiguratorApiData {
  customSegmentLengthSelected?: boolean;
  navigation?: boolean;
  reset?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ShaftConfigurationData extends LCSystemCommonConfiguratorApiData {}

export function prepareCommonConfiguratorData(
  linearState: LinearConfiguratorState,
  priceInfo?: PriceInfo | null,
): ConfiguratorApiCall {
  const groupId = linearState.groupIdToDisplay;

  const quantity = linearState.quantity;
  const price = selectPrice(quantity, priceInfo ?? null);

  let pricing = undefined;
  if (price) {
    pricing = {
      basePrice: price,
    };
  }

  const data: ConfiguratorApiCall = {
    kbKey: { productCode: linearState.productCode },
    configId: linearState.configId,
    selectedGroup: "",
    autoExpand: false,
    // groupIdToDisplay: groupId,
    quantity,
    groupToDisplay: {
      groupIdPath: `${groupId},`,
      path: "groups[0].",
    },
    groupIdToToggle: "",
    groupIdToToggleInSpecTree: "",
    groupIdToDisplay: false,
    forceExpand: false,
    hideImageGallery: false,
    pricing,
    priceReference: priceInfo?.priceReference
      ? priceInfo.priceReference
      : linearState?.priceReference
      ? linearState.priceReference
      : "",
  };

  return data;
}

type UpdateApiCallParams = Pick<
  ConfiguratorApiCall,
  "groups" | "focusId" | "cpqAction" | "groupIdToDisplay" | "groupToDisplay"
>;

export function prepareApiDataForUpdate(
  linearState: LinearConfiguratorState,
  updatedCstics: CsticUpdate[],
): UpdateApiCallParams {
  const kmats = linearState.kmats;

  function isCsticFake(update: CsticUpdate) {
    return kmats[update.type][0].cstics[update.key]?.fake;
  }

  const relevantCstics = updatedCstics
    .filter((cstic) => !isCsticFake(cstic))
    .flatMap((update) => {
      const kmats = linearState.kmats[update.type];
      let cstics;
      if (update.index === undefined) {
        cstics = kmats.map((kmat) => kmat.cstics[update.key]);
      } else {
        cstics = [kmats[update.index].cstics[update.key]];
      }
      return cstics.map((cstic) => ({
        cstic,
        value: update.value,
      }));
    });

  return formatDataForUpdate(relevantCstics);
}

function formatDataForUpdate(
  updates: { cstic: LinearCstic | undefined | null; value: string | null | undefined }[],
): UpdateApiCallParams {
  const groups = groupUpdatesForSending(updates);

  if (!groups.length) {
    throw new Error("Failed to prepare update. Updated cstics: " + JSON.stringify(updates));
  }

  return {
    groups,
    groupIdToDisplay: groups[0].id,
    groupToDisplay: {
      groupIdPath: groups[0].id,
      path: `groups[0].`,
    },
    focusId: `groups[0].cstics[0].key`,
    cpqAction: "VALUE_CHANGED",
  };
}

/**
 * Old function for preparing data for reset. It just sends all current values of cstics to backend.
 * Ran after the state is updated with default values by the reset action.
 */
export function prepareApiDataForResetOld(linearState: LinearConfiguratorState): UpdateApiCallParams {
  const relevantCstics = Object.values(linearState.kmats)
    .flatMap((kmats) => kmats)
    .flatMap((kmat) => Object.values(kmat.cstics))
    .filter((cstic) => !cstic.fake)
    .map((cstic) => ({
      cstic,
      value: cstic.value,
    }));

  return formatDataForUpdate(relevantCstics);
}

/**
 * New function for preparing data for reset.
 * It sends only default values for the given step and all subsequent ones.
 * Works only for configurators that don't have subkmats. Used only by Shaft configurators currently.
 */
export function prepareApiDataForResetNew(linearState: LinearConfiguratorState): UpdateApiCallParams {
  const currentStep = linearState.currentStep;
  const kmatsList = Object.values(linearState.kmats).flatMap((kmats) => kmats);

  const relevantCstics = linearState.steps
    .slice(currentStep)
    .flatMap((step) => {
      return Object.entries(step.defaultValues);
    })
    .flatMap(([name, value]) => {
      return kmatsList
        .filter((kmat) => kmat.cstics[name])
        .map((kmat) => {
          return {
            cstic: kmat.cstics[name],
            value: value,
          };
        });
    });

  return formatDataForUpdate(relevantCstics);
}

/**
 * Newest function for preparing data for reset.
 * It RESETS all values for the given step and all subsequent ones.
 * Works only for configurators that don't have subkmats. Used only by KLLT Carriage configurator currently.
 */
export function prepareApiDataForResetNewest(linearState: LinearConfiguratorState): UpdateApiCallParams {
  const currentStep = linearState.currentStep;
  const kmatsList = Object.values(linearState.kmats).flatMap((kmats) => kmats);

  const relevantCstics = linearState.steps
    .slice(currentStep)
    .flatMap((step) => {
      return Object.keys(step.defaultValues);
    })
    .flatMap((name) => {
      return kmatsList
        .filter((kmat) => kmat.cstics[name])
        .map((kmat) => {
          return {
            cstic: kmat.cstics[name],
            value: null,
          };
        });
    });

  return formatDataForUpdate(relevantCstics);
}

export function prepareGroupIdToDisplay(data: ConfiguratorApiCall) {
  if (data.groups && data.groups[0] && data.groups[0].id) {
    if (data.groups[0].subGroups) {
      data.groupIdToDisplay = `${data.groups[0].id}.10-BEWERTUNG`;
      data.groupToDisplay = {
        groupIdPath: `${data.groups[0].id},${data.groups[0].id}.10-BEWERTUNG`,
        path: "groups[0].subGroups[0].",
      };
    } else {
      data.groupIdToDisplay = data.groups[0].id;
      data.groupToDisplay = {
        groupIdPath: `${data.groups[0].id},`,
        path: "groups[0].",
      };
    }
  }
}

function groupUpdatesForSending(
  relevantCstics: { cstic: LinearCstic | undefined | null; value: string | null | undefined }[],
) {
  const updateEntries = relevantCstics
    .filter((entry) => isNonNull(entry.cstic) && !entry.cstic.readonly)
    .map(({ cstic, value }) => {
      const { groupId, parentGroupId, key } = cstic!;
      const csticUpdate = {
        key: `${groupId}@${key}`,
        name: key,
        value, // type mismatch here
        forceUpdate: true,
      } as CsticData;
      return {
        groupId,
        parentGroupId,
        csticUpdate,
      };
    });

  const parentGroups = Object.entries(
    groupByFn(
      updateEntries,
      (item) => item.parentGroupId ?? "",
      (item) => item,
    ),
  ).map(([parentId, cstics]) => {
    const subGroups = Object.entries(
      groupByFn(
        cstics,
        (item) => item.groupId,
        (item) => item.csticUpdate,
      ),
    ).map(([groupId, cstics]) => ({
      id: groupId,
      cstics,
    }));
    return {
      id: parentId,
      visited: true,
      subGroups,
    };
  });
  const [mainGroups, subGroups] = splitBy(parentGroups, (group) => group.id === "");
  return [...(mainGroups[0]?.subGroups || []), ...subGroups];
}
